import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import { decUsd } from "./futuresLeverageHelpers";
import { useFuturesTradeTransfer } from "./FuturesTradeTransferContext";
import {
    bindPositionMarginRatioPct,
    formatMarginRatioPct,
    isCrossMarginMode,
    isIsolatedMarginMode,
} from "./futuresSymbolMarginHelpers";
import { FuturesAccountSkeleton } from "./FuturesTradeSkeletons";

function dec(v) {
    const n = decUsd(v);
    return Number.isFinite(n) ? n : NaN;
}

function fmt4(v, suffix = "") {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    const s = parseFloat(n.toFixed(4)).toString();
    return suffix ? `${s} ${suffix}` : s;
}

function MarginGauge({ ratio }) {
    const n = Number(ratio);
    const pct = Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0;
    const color = pct >= 80 ? "#f6465d" : pct >= 50 ? "#f0b90b" : "#0ecb81";
    const rot = (pct / 100) * 270 - 135;
    return (
        <span className="agce-ft-acct-gauge" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 18 18">
                <circle cx="9" cy="9" r="7" fill="none" stroke="#e8eaed" strokeWidth="2.5" />
                <circle
                    cx="9"
                    cy="9"
                    r="7"
                    fill="none"
                    stroke={color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray={`${(pct / 100) * 44} 44`}
                    transform={`rotate(${rot} 9 9)`}
                />
            </svg>
        </span>
    );
}

function hasCrossPosition(positions) {
    return (positions || []).some((p) => isCrossMarginMode(p?.margin_mode));
}

function isolatedRows(positions) {
    return (positions || []).filter((p) => isIsolatedMarginMode(p?.margin_mode));
}

function summaryFromCrossEquity(data, accounts) {
    const marginAsset = accounts[0]?.margin_asset || "USDT";
    const pct = dec(data?.margin_ratio_pct);
    const raw = dec(data?.margin_ratio);
    // Prefer pct. Raw is a fraction (0.0577) — only × 100 when pct is missing.
    const marginRatio = Number.isFinite(pct) ? pct : Number.isFinite(raw) ? raw * 100 : NaN;
    return {
        modeLabel: "Cross",
        marginAsset,
        marginRatio,
        maintenanceMargin: dec(data?.maintenance_margin),
        marginBalance: dec(data?.equity),
        walletBalance: dec(data?.wallet ?? data?.available),
        totalUpnl: dec(data?.unrealized_pnl),
        liquidatable: !!data?.liquidatable,
    };
}

function summaryFromIsolatedRows(positions, accounts) {
    const rows = isolatedRows(positions);
    const marginAsset = accounts[0]?.margin_asset || rows[0]?.margin_asset || "USDT";
    let mm = 0;
    let upnl = 0;
    let im = 0;
    rows.forEach((p) => {
        const a = dec(p.maintenance_margin);
        const b = dec(p.unrealized_pnl);
        const c = dec(p.initial_margin);
        if (Number.isFinite(a)) mm += a;
        if (Number.isFinite(b)) upnl += b;
        if (Number.isFinite(c)) im += c;
    });
    const marginBalance = im + upnl;
    const serverRatio = rows.length === 1 ? bindPositionMarginRatioPct(rows[0]) : NaN;
    const marginRatio = Number.isFinite(serverRatio)
        ? serverRatio
        : marginBalance > 0
            ? (mm / marginBalance) * 100
            : NaN;

    let availableSum = 0;
    let lockedSum = 0;
    (accounts || []).forEach((a) => {
        const av = dec(a.available_balance);
        const lk = dec(a.locked_balance);
        if (Number.isFinite(av)) availableSum += av;
        if (Number.isFinite(lk)) lockedSum += lk;
    });

    return {
        modeLabel: "Isolated",
        marginAsset,
        marginRatio,
        maintenanceMargin: rows.length ? mm : NaN,
        marginBalance: rows.length ? marginBalance : NaN,
        walletBalance: availableSum + lockedSum,
        totalUpnl: rows.length ? upnl : NaN,
        liquidatable: false,
    };
}

/**
 * Account panel — Cross uses GET /cross-equity; Isolated-only uses that row’s fields.
 * Never leave "USDT Isolated" on a Cross book.
 */
function FuturesAccountPanel({
    token,
    futuresAccounts = [],
    futuresPositions = [],
    contractReady = true,
}) {
    const transferBridge = useFuturesTradeTransfer();
    const [assetOpen, setAssetOpen] = useState(true);
    const [crossEquity, setCrossEquity] = useState(null);
    const [equityReady, setEquityReady] = useState(false);

    const useCrossEquity = hasCrossPosition(futuresPositions) || isolatedRows(futuresPositions).length === 0;

    useEffect(() => {
        if (!token || !useCrossEquity) {
            setCrossEquity(null);
            setEquityReady(true);
            return undefined;
        }
        let cancelled = false;
        setEquityReady(false);
        const load = async () => {
            try {
                const res = await AuthService.futuresCrossEquity();
                if (!cancelled && res?.success) setCrossEquity(res.data || null);
            } catch {
                /* keep last snapshot */
            } finally {
                if (!cancelled) setEquityReady(true);
            }
        };
        load();
        const t = window.setInterval(load, 5000);
        return () => {
            cancelled = true;
            window.clearInterval(t);
        };
    }, [token, useCrossEquity, futuresPositions.length]);

    const summary = useMemo(() => {
        if (useCrossEquity && crossEquity) {
            return summaryFromCrossEquity(crossEquity, futuresAccounts);
        }
        if (!useCrossEquity) {
            return summaryFromIsolatedRows(futuresPositions, futuresAccounts);
        }
        return summaryFromIsolatedRows([], futuresAccounts);
    }, [useCrossEquity, crossEquity, futuresAccounts, futuresPositions]);

    const pnlClass = summary.totalUpnl >= 0 ? "agce-ft-acct-val--profit" : "agce-ft-acct-val--loss";

    if (!token) {
        return (
            <div className="agce-ft-acct-panel">
                <div className="agce-ft-acct-login">
                    <p>Please login to view your account</p>
                    <Link to="/login" className="agce-ft-acct-login-btn">Login</Link>
                </div>
            </div>
        );
    }

    if (!contractReady || (useCrossEquity && !equityReady)) {
        return <FuturesAccountSkeleton />;
    }

    return (
        <div className="agce-ft-acct-panel">
            <div className="agce-ft-acct-head">
                <h5 className="agce-ft-acct-title">Account</h5>
            </div>

            <div className="agce-ft-acct-section">
                <h6 className="agce-ft-acct-section-title">Margin Ratio</h6>
                <div className="agce-ft-acct-row agce-ft-acct-row--main">
                    <span>{summary.marginAsset} {summary.modeLabel}</span>
                    <span className="agce-ft-acct-row-end">
                        <MarginGauge ratio={summary.marginRatio} />
                        <strong>{formatMarginRatioPct(summary.marginRatio)}</strong>
                    </span>
                </div>
                {summary.liquidatable ? (
                    <div className="agce-ft-acct-row">
                        <span className="agce-ft-acct-label" style={{ color: "#f6465d" }}>
                            Cross wallet is at liquidation
                        </span>
                    </div>
                ) : null}
                <div className="agce-ft-acct-row">
                    <span className="agce-ft-acct-label">Maintenance Margin</span>
                    <span className="agce-ft-acct-val">{fmt4(summary.maintenanceMargin, summary.marginAsset)}</span>
                </div>
                <div className="agce-ft-acct-row">
                    <span className="agce-ft-acct-label">Margin Balance</span>
                    <span className="agce-ft-acct-val">{fmt4(summary.marginBalance, summary.marginAsset)}</span>
                </div>
            </div>

            <div className="agce-ft-acct-section agce-ft-acct-section--asset">
                <button
                    type="button"
                    className="agce-ft-acct-asset-toggle"
                    onClick={() => setAssetOpen((o) => !o)}
                    aria-expanded={assetOpen}
                >
                    <span>{summary.marginAsset}</span>
                    <i className={`ri-arrow-${assetOpen ? "up" : "down"}-s-line`} aria-hidden />
                </button>
                {assetOpen && (
                    <>
                        <div className="agce-ft-acct-row">
                            <span className="agce-ft-acct-label">Balance</span>
                            <span className="agce-ft-acct-val">{fmt4(summary.walletBalance, summary.marginAsset)}</span>
                        </div>
                        <div className="agce-ft-acct-row">
                            <span className="agce-ft-acct-label">Unrealized PNL</span>
                            <span className={`agce-ft-acct-val ${pnlClass}`}>
                                {Number.isFinite(summary.totalUpnl)
                                    ? `${summary.totalUpnl >= 0 ? "+" : ""}${fmt4(summary.totalUpnl, summary.marginAsset)}`
                                    : "—"}
                            </span>
                        </div>
                    </>
                )}
            </div>

            <div className="agce-ft-acct-actions">
                <Link to="/user_profile/wallet/futures" className="agce-ft-acct-action-btn">
                    Assets
                </Link>
                <Link to="/asset_managemnet/deposit" className="agce-ft-acct-action-btn">
                    Deposit
                </Link>
                <Link to="/user_profile/wallet/futures" className="agce-ft-acct-action-btn">Transfer</Link>
                {/* <button 
                    type="button"
                    className="agce-ft-acct-action-btn"
                    onClick={() => transferBridge?.openWalletTransfer?.()}
                >
                    Transfer
                </button> */}
            </div>
        </div>
    );
}

export default memo(FuturesAccountPanel);
